import {
  Owner,
  Advertiser,
  AdvertiserInput,
  CampaignInput,
  CampaignResponse,
  FlightInput,
  Flight,
  Store,
  ZoneResponse,
  PreviewResponse,
  CreativePreviewInput,
  FlightTargetSaveInput,
  CreativePreviewResponse,
  CreativeUploadResponse,
  FileUploadFields,
  CampaignStatus,
  CreativeUpdateInput,
  ContentCategory
} from '../types/campaign'
import { PropertyApiResponse } from '../types/store-targeting'
import { http } from './App'

const API_BASE_URL = process.env.REACT_APP_REST_API_PATH
export const gtvOwnerId = 1
export const hubspotDealRequiredOwnerIds = [1, 1576, 1998]

export const fetchOwners = async (): Promise<Owner[]> => {
  const { data } = await http.get(`${API_BASE_URL}/direct_ads/owners`, {
    withCredentials: true
  })
  return data
}

export const fetchAdvertisers = async (): Promise<Advertiser[]> => {
  const { data } = await http.get(`${API_BASE_URL}/direct_ads/advertisers`, {
    withCredentials: true
  })
  return data
}

export const fetchContentCategories = async (): Promise<ContentCategory[]> => {
  const { data } = await http.get(`${API_BASE_URL}/direct_ads/content_categories`, {
    withCredentials: true
  })
  return data
}

export const fetchStoreProperties = async (): Promise<PropertyApiResponse[]> => {
  const { data } = await http.get(`${API_BASE_URL}/store_properties`, {
    withCredentials: true
  })
  return data
}

export const createAdvertiser = async (advertiser: AdvertiserInput): Promise<Advertiser> => {
  const { data } = await http.post(`${API_BASE_URL}/direct_ads/advertisers`, advertiser, {
    withCredentials: true
  })
  return data
}

export const createCampaign = async (campaign: CampaignInput): Promise<CampaignResponse> => {
  const { data } = await http.post(`${API_BASE_URL}/direct_ads`, campaign, {
    withCredentials: true
  })
  return data
}

export const fetchCampaign = async (id: number): Promise<CampaignResponse> => {
  const { data } = await http.get(`${API_BASE_URL}/direct_ads/${id}`, {
    withCredentials: true
  })
  return data
}

export const updateCampaign = async (
  campaign: CampaignInput,
  id: number
): Promise<CampaignResponse> => {
  const { data } = await http.put(`${API_BASE_URL}/direct_ads/${id}`, campaign, {
    withCredentials: true
  })
  return data
}

export const deleteCampaign = async (id: number) => {
  const { data } = await http.post(
    `${API_BASE_URL}/direct_ads/${id}/status/delete`,
    {},
    {
      withCredentials: true
    }
  )
  return data
}

export const fetchCampaignList = async (
  status?: string[],
  owner?: string[],
  search?: string,
  page?: number
): Promise<CampaignResponse[]> => {
  const queryString = new URLSearchParams()
  if (status && status.length > 0) {
    status.forEach((item) => queryString.append('status[]', item))
  }
  if (owner && owner.length > 0) {
    owner.forEach((item) => queryString.append('owner_ids[]', item))
  }
  if (search) {
    queryString.append('search', search)
  }
  if (page) {
    queryString.append('page', (page + 1).toString())
  }
  const { data } = await http.get(
    `${API_BASE_URL}/direct_ads${queryString.toString() ? `?${queryString.toString()}` : ''}`,
    {
      withCredentials: true
    }
  )
  return data
}

export const createFlight = async (flight: FlightInput, campaignId: number): Promise<Flight> => {
  const { data } = await http.post(`${API_BASE_URL}/direct_ads/${campaignId}/flights`, flight, {
    withCredentials: true
  })
  return data
}

export const updateFlight = async (
  updatedFlight: FlightInput,
  flightId?: number
): Promise<Flight | null> => {
  if (flightId) {
    const { data } = await http.put(
      `${API_BASE_URL}/direct_ads/flights/${flightId}`,
      updatedFlight,
      {
        withCredentials: true
      }
    )
    return data
  }
  return null
}

export const deleteFlight = async (flightId: number, campaignId?: number): Promise<void> => {
  if (campaignId) {
    await http.delete(`${API_BASE_URL}/direct_ads/${campaignId}/flights/${flightId}`, {
      withCredentials: true
    })
  }
}

export const cloneFlight = async (
  flightId: number,
  campaignId: number,
  duration: { starts_at: string; ends_at: string }
): Promise<Flight> => {
  const { data } = await http.post(
    `${API_BASE_URL}/direct_ads/${campaignId}/flights/${flightId}/clone`,
    {
      flight_id: flightId,
      direct_ad_id: campaignId,
      duration
    },
    {
      withCredentials: true
    }
  )
  return data
}

export const fetchFlightStores = async (): Promise<Store[]> => {
  const { data } = await http.get(`${API_BASE_URL}/direct_ads/flights/stores`, {
    withCredentials: true
  })
  return data
}

export const updateAvailableZones = async (storeIds: number[]): Promise<ZoneResponse> => {
  const stores = { stores: storeIds.map((id) => ({ id })) }
  const { data } = await http.post(
    `${API_BASE_URL}/direct_ads/flights/target/available_zone_placements`,
    stores,
    {
      withCredentials: true
    }
  )
  return data
}

export const updateTargetingPreview = async (
  storeIds: number[],
  zoneIds: number[] | null,
  placementIds: number[] | null
): Promise<PreviewResponse | null> => {
  const stores = storeIds.map((id) => ({ id }))
  const zones = zoneIds ? zoneIds.map((id) => ({ id })) : []
  const placements = placementIds ? placementIds?.map((id) => ({ id })) : []

  if (!zones.length && !placements.length) {
    return null
  }

  const { data } = await http.post(
    `${API_BASE_URL}/direct_ads/flights/target/preview`,
    {
      stores,
      zones,
      placements
    },
    {
      withCredentials: true
    }
  )
  return data
}

export const updateCreativePreview = async (
  previewInput: CreativePreviewInput
): Promise<CreativePreviewResponse> => {
  const { data } = await http.post(
    `${API_BASE_URL}/direct_ads/flights/target/preview`,
    previewInput,
    {
      withCredentials: true
    }
  )
  return data
}

export const uploadCampaignVideo = async (
  flightId: number,
  fields: FileUploadFields,
  file: File
): Promise<CreativeUploadResponse> => {
  const formData = new FormData()

  formData.append('name', fields.name)
  // Always include these keys; send empty string when null
  formData.append('starts_at', fields.startsAt ?? '')
  formData.append('ends_at', fields.endsAt ?? '')
  formData.append('aspect_ratio', fields.aspectRatio)
  if (fields?.durationMs) {
    formData.append('duration_ms', fields.durationMs.toString())
  }
  if (fields.contentCategoryId != null) {
    formData.append('content_category_id', String(fields.contentCategoryId))
  }
  formData.append('file', file)

  const { data } = await http.post(
    `${API_BASE_URL}/direct_ads/flights/${flightId}/creatives`,
    formData,
    {
      withCredentials: true,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  )
  return data
}

export const deleteCreative = async (creativeId: number): Promise<CreativeUploadResponse> => {
  const { data } = await http.delete(`${API_BASE_URL}/direct_ads/flights/creatives/${creativeId}`, {
    withCredentials: true
  })
  return data
}

export const updateCreative = async (
  updatedCreative: CreativeUpdateInput
): Promise<CreativeUploadResponse> => {
  const { id, name, starts_at, ends_at, content_category_id } = updatedCreative
  const payload: Record<string, unknown> = { name }
  if (Object.prototype.hasOwnProperty.call(updatedCreative, 'starts_at')) {
    payload.starts_at = starts_at
  }
  if (Object.prototype.hasOwnProperty.call(updatedCreative, 'ends_at')) {
    payload.ends_at = ends_at
  }
  if (Object.prototype.hasOwnProperty.call(updatedCreative, 'content_category_id')) {
    payload.content_category_id = content_category_id
  }

  const { data } = await http.put(`${API_BASE_URL}/direct_ads/flights/creatives/${id}`, payload, {
    withCredentials: true
  })
  return data
}

export const saveFlightTargets = async (
  flightId: number,
  targets: FlightTargetSaveInput | null
): Promise<CreativePreviewResponse | null> => {
  if (!targets) {
    return null
  }
  const { data } = await http.post(
    `${API_BASE_URL}/direct_ads/flights/${flightId}/target`,
    targets,
    {
      withCredentials: true
    }
  )
  return data
}

export const updateCampaignStatus = async (campaignId: number, status: string) => {
  const statusParam = status === CampaignStatus.Scheduled ? 'schedule' : status
  const { data } = await http.post(
    `${API_BASE_URL}/direct_ads/${campaignId}/status/${statusParam}`,
    {},
    { withCredentials: true }
  )
  return data
}

export const scheduleFlight = async (flightId: number, status: string) => {
  if (status === CampaignStatus.Scheduled) {
    const { data } = await http.post(
      `${API_BASE_URL}/direct_ads/flights/${flightId}/status/schedule`,
      {},
      { withCredentials: true }
    )
    return data
  }
}
